import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Autocomplete, Stack, TextField, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'

import { CUTTING_TECHNOLOGY_POWERS } from '@/common/utils'

import { FORM_FIELDS, TECHNICAL_LABELS, TEST_IDS } from '../constants/formConstants'

const TechnicalTypography = styled(Typography)({
    letterSpacing: '0.5px',
})

const classes = {
    input: {},
    autoComplete: {},
    inputLabel: {},
}

/**
 * AccountDetailsSection - Personal and organization account information fields
 *
 * Collects:
 * - Contact first name and last name
 * - Organization name
 * - Cutting technology power
 * - Country selection
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control object
 * @param {Object} props.theme - MUI theme object
 * @param {Array<Object>} props.countryList - List of countries from country-locale-map
 */
export const AccountDetailsSection = ({ control, countryList, theme }) => {
    const { t } = useTranslation()

    return (
        <Stack spacing={2}>
            <TechnicalTypography
                color={theme.palette.text.secondary}
                sx={{ textTransform: 'uppercase', mb: 1 }}
                variant="strong2"
            >
                {t(TECHNICAL_LABELS.SECTION_ACCOUNT)}
            </TechnicalTypography>

            {/* First Name */}
            <Controller
                control={control}
                name={FORM_FIELDS.CONTACT_FIRST_NAME}
                render={({ field: { onBlur, onChange, value }, fieldState }) => (
                    <TextField
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message || ''}
                        inputProps={{
                            sx: classes.input,
                            'data-testid': TEST_IDS.FIRST_NAME,
                        }}
                        label={t('First name')}
                        sx={classes.input}
                        value={value}
                        fullWidth
                        onBlur={onBlur}
                        onChange={onChange}
                    />
                )}
                rules={{ required: t('First name is required') }}
            />

            {/* Last Name */}
            <Controller
                control={control}
                name={FORM_FIELDS.CONTACT_LAST_NAME}
                render={({ field: { onBlur, onChange, value }, fieldState }) => (
                    <TextField
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message || ''}
                        inputProps={{
                            sx: classes.input,
                            'data-testid': TEST_IDS.LAST_NAME,
                        }}
                        label={t('Last name')}
                        sx={classes.input}
                        value={value}
                        fullWidth
                        onBlur={onBlur}
                        onChange={onChange}
                    />
                )}
                rules={{ required: t('Last name is required') }}
            />

            {/* Organization Name */}
            <Controller
                control={control}
                name={FORM_FIELDS.ORGANISATION_NAME}
                render={({ field: { onBlur, onChange, value }, fieldState }) => (
                    <TextField
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message || ''}
                        inputProps={{
                            sx: classes.input,
                            'data-testid': TEST_IDS.ORGANISATION_NAME,
                        }}
                        label={t('Organisation name')}
                        sx={classes.input}
                        value={value}
                        fullWidth
                        onBlur={onBlur}
                        onChange={onChange}
                    />
                )}
                rules={{ required: t('Organisation name is required') }}
            />

            {/* Cutting Technology Power */}
            <Controller
                control={control}
                name={FORM_FIELDS.ORGANISATION_CUTTING_TECH_POWER}
                render={({ field: { onBlur, onChange, value }, fieldState }) => (
                    <Autocomplete
                        className={classes.autoComplete}
                        disableClearable={true}
                        getOptionLabel={(option) => option.toString()}
                        options={CUTTING_TECHNOLOGY_POWERS}
                        renderInput={(params) => (
                            <TextField
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message || ''}
                                placeholder={t('4kW')}
                                {...params}
                                InputLabelProps={{
                                    sx: classes.inputLabel,
                                    'data-testid': TEST_IDS.CUTTING_TECH_POWER_LABEL,
                                }}
                                inputProps={{
                                    ...params.inputProps,
                                    'data-testid': TEST_IDS.CUTTING_TECH_POWER,
                                }}
                                label={t('Cutting technology power (kW)')}
                                fullWidth
                            />
                        )}
                        value={value}
                        fullWidth
                        onBlur={onBlur}
                        onChange={(e, newValue) => onChange(newValue)}
                    />
                )}
                rules={{ required: t('Cutting technology power is required') }}
            />

            {/* Country */}
            <Controller
                control={control}
                name={FORM_FIELDS.ORGANISATION_COUNTRY}
                render={({ field: { onBlur, onChange, value }, fieldState }) => (
                    <Autocomplete
                        className={classes.autoComplete}
                        disableClearable={true}
                        getOptionLabel={(option) => option.name || ''}
                        options={countryList}
                        renderInput={(params) => (
                            <TextField
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message || ''}
                                {...params}
                                InputLabelProps={{
                                    sx: classes.inputLabel,
                                    'data-testid': TEST_IDS.COUNTRY_LABEL,
                                }}
                                inputProps={{
                                    ...params.inputProps,
                                    'data-testid': TEST_IDS.COUNTRY_INPUT,
                                }}
                                label={t('Country')}
                            />
                        )}
                        value={value}
                        fullWidth
                        onBlur={onBlur}
                        onChange={(e, newValue) => {
                            onChange(newValue)
                        }}
                    />
                )}
                rules={{ required: t('Country is required') }}
            />
        </Stack>
    )
}

AccountDetailsSection.propTypes = {
    control: PropTypes.object.isRequired,
    countryList: PropTypes.array.isRequired,
    theme: PropTypes.object.isRequired,
}
